import React, { lazy } from 'react';
import { PrivateOutlet } from '../base-react/components/PrivateOutlet';
import { OrganizationAdminOutlet } from './components/OrganizationAdminOutlet';
import { RouteBoundary } from './components/RouteBoundary';
import { CardSkeleton, PageSkeleton } from './components/Skeletons';
const LazyDashboard = lazy(() => import('./components/Dashboard').then((module) => ({ default: module.Dashboard })));
const LazyLandingPage = lazy(() => import('./pages/landing').then((module) => ({ default: module.LandingPage })));
const LazyLegacyRedirect = lazy(() => import('./pages/redirect').then((module) => ({ default: module.LegacyRedirect })));
const LazyAbout = lazy(() => import('./pages/about').then((module) => ({ default: module.About })));
const LazyPrivacyPolicy = lazy(() => import('./pages/policy').then((module) => ({ default: module.PrivacyPolicy })));
const LazyTermsOfService = lazy(() => import('./pages/policy').then((module) => ({ default: module.TermsOfService })));
const LazyAuthorizeClient = lazy(() => import('./pages/authorize').then((module) => ({ default: module.AuthorizeClient })));
const LazyRegistration = lazy(() => import('./pages/competition/registration').then((module) => ({
    default: module.Registration,
})));
const LazyResetPassword = lazy(() => import('./pages/password').then((module) => ({ default: module.ResetPassword })));
const LazyRegistrationSuccess = lazy(() => import('./pages/competition/registration/success').then((module) => ({
    default: module.RegistrationSuccess,
})));
const LazyRegistrationLater = lazy(() => import('./pages/competition/registration/later').then((module) => ({
    default: module.RegistrationLater,
})));
const LazyRegistrationCancel = lazy(() => import('./pages/competition/registration/cancel').then((module) => ({
    default: module.RegistrationCancel,
})));
const LazyRegistrationClassesPublic = lazy(() => import('./pages/event/registration/RegistrationClassesPublic').then((module) => ({
    default: module.default,
})));
const LazyEventShell = lazy(() => import('./pages/event/EventShell').then((module) => ({ default: module.EventShell })));
const LazyRegistrationRedirect = lazy(() => import('./pages/event/EventShell').then((module) => ({
    default: module.RegistrationRedirect,
})));
const LazyOrganizationLayout = lazy(() => import('./components/OrganizationLayout').then((module) => ({
    default: module.OrganizationLayout,
})));
const LazyOrganizationList = lazy(() => import('./pages/organization').then((module) => ({ default: module.OrganizationList })));
const LazyOrganizationHome = lazy(() => import('./pages/organization/home').then((module) => ({ default: module.OrganizationHome })));
const LazyOrganizationAdmin = lazy(() => import('./pages/organization/admin').then((module) => ({
    default: module.OrganizationAdmin,
})));
const LazyOrganizationMembers = lazy(() => import('./pages/organization/members').then((module) => ({
    default: module.OrganizationMembers,
})));
const LazyOrganizationTeams = lazy(() => import('./pages/organization/teams').then((module) => ({ default: module.OrganizationTeams })));
const LazyOrganizationRegistrations = lazy(() => import('./pages/organization/registrations').then((module) => ({
    default: module.OrganizationRegistrations,
})));
const LazyOrganizationFederation = lazy(() => import('./pages/organization/federation').then((module) => ({
    default: module.OrganizationFederation,
})));
const LazyFederationList = lazy(() => import('./pages/federation').then((module) => ({ default: module.FederationList })));
const LazyFederationHome = lazy(() => import('./pages/federation/home').then((module) => ({ default: module.FederationHome })));
const LazyFederationRankingList = lazy(() => import('./pages/federation/ranking').then((module) => ({
    default: module.FederationRankingList,
})));
const LazyFederationRankingTeams = lazy(() => import('./pages/federation/ranking').then((module) => ({
    default: module.FederationRankingTeams,
})));
const LazyDanceTeamSearch = lazy(() => import('./pages/team').then((module) => ({ default: module.DanceTeamSearch })));
const LazyDanceTeamProfile = lazy(() => import('./pages/team/profile').then((module) => ({
    default: module.DanceTeamProfile,
})));
const LazyAccount = lazy(() => import('./pages/account').then((module) => ({ default: module.Account })));
const LazyAccountSettings = lazy(() => import('./pages/account/settings').then((module) => ({
    default: module.AccountSettings,
})));
const LazySponsorCheckout = lazy(() => import('./pages/sponsor/checkout').then((module) => ({
    default: module.SponsorCheckout,
})));
const LazySponsorSite = lazy(() => import('./pages/sponsor').then((module) => ({
    default: module.SponsorSite,
})));
const LazySponsorClaim = lazy(() => import('./pages/sponsor/claim').then((module) => ({
    default: module.SponsorClaim,
})));
const pageFallback = React.createElement(PageSkeleton, null);
const cardFallback = React.createElement(CardSkeleton, null);
const withFallback = (element, fallback = pageFallback) => React.createElement(RouteBoundary, { element: element, fallback: fallback });
const accountRoutes = {
    path: 'account',
    element: withFallback(React.createElement(PrivateOutlet, null), cardFallback),
    handle: { titleKey: 'profile.title' },
    children: [
        {
            index: true,
            element: withFallback(React.createElement(LazyAccount, null), cardFallback),
            handle: { titleKey: 'profile.title' },
        },
        {
            path: 'settings',
            element: withFallback(React.createElement(LazyAccountSettings, null), cardFallback),
            handle: { titleKey: 'common.settings' },
        },
    ],
};
const organizationAdminRoutes = [
    {
        path: 'admin',
        element: withFallback(React.createElement(LazyOrganizationAdmin, null), cardFallback),
        handle: { titleKey: 'organization.admin.title' },
    },
    {
        path: 'member',
        element: withFallback(React.createElement(LazyOrganizationMembers, null), cardFallback),
        handle: { titleKey: 'organization.member.plural' },
    },
    {
        path: 'teams',
        element: withFallback(React.createElement(LazyOrganizationTeams, null), cardFallback),
        handle: { titleKey: 'organization.title' },
    },
    {
        path: 'registrations',
        element: withFallback(React.createElement(LazyOrganizationRegistrations, null), cardFallback),
        handle: { titleKey: 'registration.plural' },
    },
    {
        path: 'federation',
        element: withFallback(React.createElement(LazyOrganizationFederation, null), cardFallback),
        handle: { titleKey: 'federation.title' },
    },
];
const organizationRoutes = {
    path: 'organization',
    handle: { titleKey: 'organization.plural' },
    children: [
        {
            index: true,
            element: withFallback(React.createElement(LazyOrganizationList, null), cardFallback),
            handle: { titleKey: 'organization.plural' },
        },
        {
            path: ':organization_id',
            element: withFallback(React.createElement(LazyOrganizationLayout, null), cardFallback),
            handle: { titleKey: 'organization.title' },
            children: [
                {
                    index: true,
                    element: withFallback(React.createElement(LazyOrganizationHome, null), cardFallback),
                    handle: { titleKey: 'organization.home.title' },
                },
                {
                    element: withFallback(React.createElement(OrganizationAdminOutlet, null), cardFallback),
                    children: organizationAdminRoutes,
                },
            ],
        },
    ],
};
const federationRoutes = {
    path: 'federation',
    handle: { titleKey: 'federation.plural' },
    children: [
        {
            index: true,
            element: withFallback(React.createElement(LazyFederationList, null), cardFallback),
            handle: { titleKey: 'federation.plural' },
        },
        {
            path: ':federation_id',
            handle: { titleKey: 'federation.title' },
            children: [
                {
                    index: true,
                    element: withFallback(React.createElement(LazyFederationHome, null), cardFallback),
                    handle: { titleKey: 'federation.title' },
                },
                {
                    path: 'ranking',
                    element: withFallback(React.createElement(LazyFederationRankingList, null), cardFallback),
                    handle: { titleKey: 'ranking.plural' },
                },
                {
                    path: 'division/:division_id/ranking/:ranking_id',
                    element: withFallback(React.createElement(LazyFederationRankingTeams, null), cardFallback),
                    handle: { titleKey: 'ranking.title' },
                },
            ],
        },
    ],
};
const competitionRoutes = {
    path: 'comp/:competition_id',
    children: [
        {
            path: 'results',
            element: null,
            children: [
                {
                    path: ':class_id',
                    element: null,
                },
            ],
        },
        {
            path: 'teams',
            element: null,
            children: [
                {
                    path: ':class_id',
                    element: null,
                },
            ],
        },
        {
            element: withFallback(React.createElement(PrivateOutlet, null), cardFallback),
            children: [
                {
                    path: 'registration',
                    element: withFallback(React.createElement(LazyRegistration, null), cardFallback),
                    handle: { titleKey: 'registration.title' },
                },
                {
                    path: 'registration/success',
                    element: withFallback(React.createElement(LazyRegistrationSuccess, null), cardFallback),
                    handle: { titleKey: 'registration.done.title' },
                },
                {
                    path: 'registration/cancel',
                    element: withFallback(React.createElement(LazyRegistrationCancel, null), cardFallback),
                },
                {
                    path: 'registration/cancel/:session_id',
                    element: withFallback(React.createElement(LazyRegistrationCancel, null), cardFallback),
                },
                {
                    path: 'registration/later',
                    element: withFallback(React.createElement(LazyRegistrationLater, null), cardFallback),
                    handle: { titleKey: 'registration.later.title' },
                },
            ],
        },
    ],
};
const eventRoutes = {
    path: 'event/:event_id',
    element: withFallback(React.createElement(LazyEventShell, null), pageFallback),
    children: [
        {
            index: true,
            element: null,
        },
        {
            path: 'overview',
            element: null,
        },
        {
            path: 'info',
            element: null,
        },
        {
            path: 'schedule',
            element: null,
        },
        {
            path: 'schedule/:schedule_id/:round_id',
            element: null,
        },
        {
            path: 'registration',
            element: withFallback(React.createElement(LazyRegistrationRedirect, null), cardFallback),
        },
        {
            path: 'sponsors',
            element: withFallback(React.createElement(LazySponsorSite, null), cardFallback),
            handle: { titleKey: 'sponsor.site.title' },
        },
        {
            path: 'sponsors/claim/:claim_token',
            element: withFallback(React.createElement(LazySponsorClaim, null), cardFallback),
            handle: { titleKey: 'sponsor.claim.title' },
        },
        {
            path: 'sponsors/:sponsor_item_id',
            element: withFallback(React.createElement(LazySponsorCheckout, null), cardFallback),
            handle: { titleKey: 'sponsor.checkout.title' },
        },
        {
            path: 'registration-classes',
            element: withFallback(React.createElement(LazyRegistrationClassesPublic, null), cardFallback),
        },
        {
            path: ':schedule_id/:round_id',
            element: null,
        },
        competitionRoutes,
    ],
};
const teamRoutes = {
    path: 'team',
    handle: { titleKey: 'danceteam.plural' },
    children: [
        {
            index: true,
            element: withFallback(React.createElement(LazyDanceTeamSearch, null), cardFallback),
            handle: { titleKey: 'danceteam.plural' },
        },
        {
            path: ':dance_team_id',
            element: withFallback(React.createElement(LazyDanceTeamProfile, null), cardFallback),
            handle: { titleKey: 'danceteam.title' },
        },
    ],
};
export const publicRoutes = [
    {
        element: withFallback(React.createElement(LazyDashboard, null), pageFallback),
        children: [
            { index: true, element: withFallback(React.createElement(LazyLandingPage, null)) },
            {
                path: 'redirect',
                element: withFallback(React.createElement(LazyLegacyRedirect, null), cardFallback),
            },
            {
                path: 'redirect/*',
                element: withFallback(React.createElement(LazyLegacyRedirect, null), cardFallback),
            },
            {
                path: 'authorize/:client_id/*',
                element: withFallback(React.createElement(LazyAuthorizeClient, null), cardFallback),
                handle: { titleKey: 'oauth.title' }
            },
            {
                path: 'password/:token',
                element: withFallback(React.createElement(LazyResetPassword, null), cardFallback),
                handle: { titleKey: 'login.reset.title' }
            },
            {
                path: 'about',
                element: withFallback(React.createElement(LazyAbout, null)),
                handle: { titleKey: 'about.title' }
            },
            {
                path: 'terms',
                element: withFallback(React.createElement(LazyTermsOfService, null), cardFallback),
                handle: { titleKey: 'register.terms' }
            },
            {
                path: 'privacy',
                element: withFallback(React.createElement(LazyPrivacyPolicy, null), cardFallback),
                handle: { titleKey: 'register.privacy' }
            },
            accountRoutes,
            organizationRoutes,
            federationRoutes,
            eventRoutes,
            teamRoutes,
        ],
    },
];
